import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import styled from "styled-components";

import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";


// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, LineElement);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
     padding: 20px;
`;


const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  z-index: 10;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const OuterWrapper = styled.div`
  position: relative;
`;


const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const ScatterPlotSales = ({ data }) => {
  const [salesData, setSalesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const firstDay = new Date(date.getFullYear(), 0, 1);
  const dayOfYear = ((date - firstDay + 86400000) / 86400000);
  return Math.ceil(dayOfYear / 7);
};


  const calculateMonthlySales = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

useEffect(() => {
  if (data && data.length > 0) {
    const grouped = data.reduce((acc, row) => {
      const year = parseInt(row.Rok, 10);
      const week = getWeekNumber(row.DataWystawienia);
      const sales = calculateMonthlySales(row); // ✅ DEFINE sales here

      if (!acc[year]) acc[year] = {};
      if (!acc[year][week]) acc[year][week] = 0;
      acc[year][week] += sales;

      return acc;
    }, {});

    const years = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    setAvailableYears(years);

    if (!selectedYear) {
      setSelectedYear(years[years.length - 1]);
    }

    setSalesData(grouped);
  }
}, [data, selectedYear]);


  useEffect(() => {
    if (!selectedYear || !salesData[selectedYear]) return;

    const currentObj = salesData[selectedYear];
const previousObj = salesData[selectedYear - 1] || {};

const weeks = [...new Set([
  ...Object.keys(currentObj || {}),
  ...Object.keys(previousObj || {})
])].map(Number).sort((a, b) => a - b);

const current = weeks.map(w => currentObj[w] || 0);
const previous = weeks.map(w => previousObj[w] || null);

const avg = current.reduce((sum, val) => sum + val, 0) / current.length;

const GREEN  = "#2e7d32";
const ORANGE = "#fb8c00";
const RED    = "#d32f2f";
const BLUE   = "#1976d2";

const scatterData = weeks.map((week, i) => {
  const sales = current[i];
  return {
    x: week,
    y: sales,
    backgroundColor:
      sales > avg * 1.2 ? GREEN : sales < avg * 0.8 ? RED : ORANGE,
  };
});

setChartData({
  datasets: [
    {
      label: "Powyżej Średniej (+20%)",
      data: scatterData.filter((d) => d.backgroundColor === GREEN),
      backgroundColor: GREEN,
    },
    {
      label: "Blisko Średniej",
      data: scatterData.filter((d) => d.backgroundColor === ORANGE),
      backgroundColor: ORANGE,
    },
    {
      label: "Poniżej Średniej (-20%)",
      data: scatterData.filter((d) => d.backgroundColor === RED),
      backgroundColor: RED,
    },
    {
      label: "Średnia Sprzedaż",
      data: Array(12).fill(avg).map((y, i) => ({ x: i + 1, y })),
      type: "line",
      borderColor: BLUE,          // ✅ average line = blue
      borderWidth: 3,
      borderDash: [10, 5],
      fill: false,
      pointRadius: 0,
    },
    {
      label: `Poprzedni Rok (${selectedYear - 1})`,
      data: previous.map((y, i) => (y !== null ? { x: i + 1, y } : null)).filter(Boolean),
      type: "line",
      borderColor: ORANGE,        // ✅ previous year trend = orange
      backgroundColor: "rgba(251,140,0,0.3)",
      borderWidth: 2,
      pointStyle: "circle",
      pointRadius: 3,
      fill: false,
    },
  ],
});

  }, [selectedYear, salesData]);

  if (!availableYears.length || !chartData) return <p>Ładowanie danych...</p>;

  return (

      <OuterWrapper>
    <InfoIconWrapper data-tooltip-id="scatter-sales-tooltip">
      <Info />
    </InfoIconWrapper>
    <InfoTooltip
      id="scatter-sales-tooltip"
      place="left"
      content={
        <>
          <strong>Analiza tygodniowa</strong><br />
          Średnia sprzedaż tygodniowa<br />
          z podziałem na tygodnie roku {selectedYear} i {selectedYear - 1}.
        </>
      }
    />
    <ChartWrapper>

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Scatter
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
           x: {
  title: {
    display: true,
    text: "Tydzień",
  },
  ticks: {
    callback: (val) => `Tydzień ${val}`,
  },
},
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Sprzedaż",
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Porównanie średniej dla dnia tygodnia ${selectedYear} i Porównanie z ${selectedYear - 1}`,
            },
            tooltip: {
              callbacks: {
label: (ctx) =>
  `Sprzedaż: ${ctx.raw.y?.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`,
              },
            },
            legend: {
              display: true,
              onClick: (e, legendItem, legend) => {
                const index = legendItem.datasetIndex;
                const chart = legend.chart;

                if (index !== undefined) {
                  const meta = chart.getDatasetMeta(index);
                  meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                  chart.update();
                }
              },
            },
          },
        }}
      />
    </ChartWrapper>
    </OuterWrapper>
  );
};

export default ScatterPlotSales;
